/**
 * The three plan features that make up the Sites entitlement.
 *
 * Gating is by feature NAME: the API flattens an org's plan features and org
 * feature flags into `user.features` as a list of `features.name` strings (see
 * services/passport lookUpUserFromJWTPayload), and the frontend matches against
 * that. Entitlements themselves live in `plan_features`, which links by
 * `feature_id` — so renaming a feature moves no entitlement, it only changes
 * the string these checks have to match.
 *
 * BIR-370 renamed two of them: `gcp-processing` → `gcp` and
 * `prism-processing` → `prism`. Despite the old names neither gates the
 * processing pipeline — they gate Sites screens (the Reference sets tab, the
 * prism set, the Tolerances tab), which is why they read as duplicates of the
 * genuine `processing-gcps` in the ops feature list.
 *
 * The legacy aliases carried the deploy window, when the DB and the code could
 * disagree. UAT and PROD are both renamed now, and `seeds/features.js` seeds
 * the new names, so they are gone.
 *
 * That makes the runbook's rollback SQL a code-coupled operation: reverting the
 * names in the DB alone would now lock entitled orgs out of the Reference sets
 * and Tolerances tabs. Revert the code with it.
 */

/** `meta.features` is a plain array; the next-map store's is readonly. */
type Features = readonly string[] | undefined;

/** Sites hub, sidebar entry, site cards in the Map Browser. Not renamed. */
export const SITES_FEATURE = 'map-sites';

/** Reference sets as a whole — the tab, and adding a set to a map. */
export const GCP_FEATURE = 'gcp';

/** The prism set only, plus tolerances and the prism report. */
export const PRISM_FEATURE = 'prism';

const has = (features: Features, ...names: string[]): boolean =>
  !!features && names.some((name) => features.includes(name));

export const hasSites = (features: Features): boolean =>
  has(features, SITES_FEATURE);

export const hasGcp = (features: Features): boolean =>
  has(features, GCP_FEATURE);

export const hasPrism = (features: Features): boolean =>
  has(features, PRISM_FEATURE);

/**
 * TEMPORARY staged-rollout ORG FLAG (BIR-22), not a plan feature.
 *
 * Read from an org-flag array, never from `features`: the map's websocket
 * identity keeps plan features and org flags in two separate arrays, so a flag
 * looked up in `features` is silently always false. (REST `meta.features`
 * merges them, which is exactly what makes this easy to get wrong.)
 *
 * It sits in FRONT of the plan features, gating only what is new and unproven
 * in production — the site-sourced sections of the processing form, and the
 * prism trend report. It deliberately does NOT gate the Sites screens that are
 * already live: the Reference sets tab, the prism set, the Tolerances tab.
 *
 * TO REMOVE once the flow is proven: delete this and every caller. What is
 * left underneath — `gcp`, `prism`, `processing-gcps` — is the intended end
 * state, so removal is a pure subtraction.
 */
export const SITES_ROLLOUT_FLAG = 'sites-gcp-prism-processing';

export const hasSitesRollout = (orgFeatureFlags: Features): boolean =>
  has(orgFeatureFlags, SITES_ROLLOUT_FLAG);

/** Reference sets come in both kinds; each dims on its own entitlement. */
export const hasReferenceSetKind = (
  features: Features,
  kind: string,
): boolean => (kind === 'prism' ? hasPrism(features) : hasGcp(features));
